import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiTrash2, FiEye, FiZap, FiPlusCircle, FiFileText, FiEdit2 } from "react-icons/fi";
import TemplatePreviewModal from "../components/TemplatePreviewModal";
import { fetchEmailTemplatesList, createEmailTemplate, deleteEmailTemplate } from "../api/contacts";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const STARTER_TEMPLATES = [
  {
    name: "Quick Question (25%+ Reply Rate)",
    subject: "Quick question regarding {company_name}",
    body: `Hi {name},\n\nI came across {company_name} while researching top {category} companies in {area}.\n\nI had a quick idea on how you could get 5-10 extra client inquiries every week without spending extra on ads.\n\nWould you be open to a short 2-minute chat or should I send a 60-second video explaining the idea?\n\nBest regards,\nHamza`
  },
  {
    name: "Case Study & Results (High Conversion)",
    subject: "Idea for {company_name}'s client growth",
    body: `Hi {name},\n\nI was looking into {company_name} and noticed your work in {services_offered}.\n\nWe recently helped a similar business in your industry increase their qualified client bookings by 35% in under 30 days.\n\nI put together 3 specific suggestions tailored for {company_name}. Are you open for a quick 5-minute call this week to go over them?\n\nBest regards,\nHamza`
  },
  {
    name: "1-Sentence Soft Pitch (Busy Founders)",
    subject: "1 quick question for {name}",
    body: `Hi {name},\n\nAre you currently open to taking on more new clients for {company_name}?\n\nIf so, I'd love to share how we can deliver 10+ verified leads directly to your inbox each week.\n\nLet me know if you'd like me to send over a quick 1-page overview.\n\nBest regards,\nHamza`
  },
  {
    name: "Compliment & Value Pitch",
    subject: "Impressed by {company_name}",
    body: `Hi {name},\n\nI was researching {category} providers in {area} and was really impressed by {company_name}.\n\nWe specialize in helping companies like yours build automated, predictable client acquisition systems.\n\nWould you be open to a quick chat this Thursday to see if we might be a fit?\n\nThanks,\nHamza`
  },
  {
    name: "Gentle Follow-Up (Doubles Response)",
    subject: "Re: Quick question regarding {company_name}",
    body: `Hi {name},\n\nFollowing up on my previous email. I know you're super busy managing {company_name}.\n\nJust wanted to check if acquiring more clients is a priority for you right now?\n\nIf not, no worries at all!\n\nBest regards,\nHamza`
  }
];

export default function TemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { token } = useAuth();

  const loadTemplates = useCallback(async () => {
    if (!token?.access) return;
    try {
      setLoading(true);
      setError("");
      const data = await fetchEmailTemplatesList(token.access);
      setTemplates(Array.isArray(data) ? data : [data]);
    } catch (err) {
      console.error("Failed to fetch templates:", err.message);
      setError(err.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleSeedStarterPack = async () => {
    if (!token?.access) return;
    setSeeding(true);
    try {
      for (const t of STARTER_TEMPLATES) {
        await createEmailTemplate(token.access, t.name, t.subject, t.body);
      }
      toast.success("5 Proven High-Converting Templates added!");
      await loadTemplates();
    } catch (err) {
      toast.error("Failed to seed templates: " + err.message);
    } finally {
      setSeeding(false);
    }
  };

  const handleDeleteTemplate = async (id, name) => {
    if (!token?.access) return;
    const confirmDelete = window.confirm(
      `Are you sure you want to delete template "${name || "this template"}"? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    setDeletingId(id);
    try {
      await deleteEmailTemplate(token.access, id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Template deleted successfully!");
    } catch (err) {
      console.error("Failed to delete template:", err.message);
      toast.error(err.message || "Failed to delete template");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-12 flex flex-col items-center justify-center text-gray-500">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-r-transparent mb-3"></div>
        <p className="text-sm font-medium">Loading templates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={loadTemplates}
            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 rounded font-medium text-xs transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Heading & Action */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiFileText className="text-blue-600" /> Templates List ({templates.length})
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage your cold outreach email templates, edit dynamic copy, or load proven presets
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/templates/create"
            className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition shadow-sm"
          >
            <FiPlusCircle size={18} /> Create Template
          </Link>
          <button
            onClick={handleSeedStarterPack}
            disabled={seeding}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2 transition disabled:opacity-50 shadow-sm"
          >
            <FiZap />
            {seeding ? "Adding Starter Pack..." : "Add 5 Proven Outreach Templates"}
          </button>
        </div>
      </div>

      {/* Empty State */}
      {templates.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center max-w-lg mx-auto">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiFileText size={26} />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-1">No templates yet</h3>
          <p className="text-sm text-gray-500 mb-6">
            Get started by creating your custom email template or adding our 5 high-converting starter templates.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/dashboard/templates/create"
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
            >
              Create First Template
            </Link>
            <button
              onClick={handleSeedStarterPack}
              disabled={seeding}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition disabled:opacity-50"
            >
              Load 5 Proven Templates
            </button>
          </div>
        </div>
      ) : (
        /* Grid of templates */
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 hover:shadow-md hover:border-gray-300 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-base font-semibold text-gray-900 line-clamp-1" title={template.name}>
                    {template.name}
                  </h3>
                  <span className="shrink-0 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[11px] font-medium">
                    ID #{template.id}
                  </span>
                </div>

                <div className="mb-3">
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">
                    Subject Line:
                  </span>
                  <p className="text-xs text-gray-600 line-clamp-2 font-medium bg-gray-50 p-2 rounded border border-gray-100">
                    {template.subject || <span className="italic text-gray-400">No subject</span>}
                  </p>
                </div>

                <div>
                  <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider block mb-0.5">
                    Body Snippet:
                  </span>
                  <p className="text-xs text-gray-500 line-clamp-3 leading-relaxed">
                    {template.body || <span className="italic text-gray-400">No body content</span>}
                  </p>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-[11px] text-gray-400">
                  {template.created_at
                    ? new Date(template.created_at).toLocaleDateString()
                    : ""}
                </span>

                <div className="flex items-center gap-2">
                  {/* Preview Button */}
                  <button
                    onClick={() => setPreview(template)}
                    className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-md transition"
                    title="Live Preview & Test"
                  >
                    <FiEye size={16} />
                  </button>

                  {/* Edit Button */}
                  <Link
                    to={`/dashboard/templates/edit/${template.id}`}
                    className="p-1.5 text-gray-600 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition flex items-center gap-1 text-xs font-semibold"
                    title="Edit Template"
                  >
                    <FiEdit2 size={15} />
                    <span>Edit</span>
                  </Link>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeleteTemplate(template.id, template.name)}
                    disabled={deletingId === template.id}
                    className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition disabled:opacity-50"
                    title="Delete Template"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <TemplatePreviewModal
          template={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

